import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  applyMapImportFix,
  importFailureMessage,
  importMapDocument,
  type MapDocument,
  type MapImportNote,
} from '../../sim/map/index.ts';
import { setPanel } from '../panels.ts';
import { reportMapImportFailure } from './dialog.tsx';
import './dialog.css';

export type OpenMapEntry = {
  id: string;
  label: string;
};

export type OpenMapChoice =
  | { kind: 'catalog'; id: string }
  | { kind: 'import'; map: MapDocument; fileName: string; notes: readonly MapImportNote[] };

export type EditorOpenMapProps = {
  maps: readonly OpenMapEntry[];
  onSelect: (id: string) => void;
  onImport: (map: MapDocument, fileName: string, notes: readonly MapImportNote[]) => void;
  onImportError: (message: string) => void;
  onCancel: () => void;
};

export function EditorOpenMapPanel({
  maps,
  onSelect,
  onImport,
  onImportError,
  onCancel,
}: EditorOpenMapProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const readImport = (file: File): void => {
    const reader = new FileReader();

    reader.onload = () => {
      void (async () => {
        const text = typeof reader.result === 'string' ? reader.result : '';
        const result = importMapDocument(text);

        if (result.ok) {
          onImport(result.map, file.name, result.notes);
          return;
        }

        const message = importFailureMessage(result);
        setError(message);
        onImportError(message);

        if (!result.fix) {
          return;
        }

        const accepted = await reportMapImportFailure(result);

        if (!accepted) {
          return;
        }

        const fixed = applyMapImportFix(text, result.fix.id);

        if (!fixed.ok) {
          const next = importFailureMessage(fixed);
          setError(next);
          onImportError(next);
          return;
        }

        onImport(fixed.map, file.name, fixed.notes);
      })();
    };

    reader.onerror = () => {
      const message = 'Could not read that map file.';
      setError(message);
      onImportError(message);
    };

    reader.readAsText(file);
  };

  return (
    <div
      class="editor-dialog-backdrop ui-chrome"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onCancel();
        }
      }}
    >
      <div class="editor-dialog editor-open-dialog ui-panel" role="dialog" aria-modal="true" aria-label="Open map">
        <h2 class="editor-dialog-title">Open map</h2>
        <p class="ui-hint">Choose a catalog map, or import a JSON file.</p>
        <div class="editor-open-list">
          {maps.length === 0 ? (
            <p class="ui-hint">No maps found.</p>
          ) : (
            maps.map((map) => (
              <button
                key={map.id}
                type="button"
                class="editor-open-item"
                data-map-open={map.id}
                onClick={() => onSelect(map.id)}
              >
                <span class="editor-open-item-label">{map.label}</span>
                <span class="ui-hint">{map.id}</span>
              </button>
            ))
          )}
        </div>
        {error ? <p class="ui-hint editor-open-error">{error}</p> : null}
        <div class="editor-dialog-actions">
          <input
            ref={inputRef}
            type="file"
            accept="application/json"
            data-map-import
            class="editor-open-import"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              event.currentTarget.value = '';

              if (file) {
                readImport(file);
              }
            }}
          />
          <button type="button" class="ui-button" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            class="ui-button"
            onClick={() => inputRef.current?.click()}
          >
            Import…
          </button>
        </div>
      </div>
    </div>
  );
}

export function promptOpenMap(
  maps: readonly OpenMapEntry[],
  options: { onError?: (message: string) => void } = {},
): Promise<OpenMapChoice | null> {
  return new Promise((resolve) => {
    const settle = (choice: OpenMapChoice | null): void => {
      setPanel('editorOpenMap', null);
      resolve(choice);
    };

    setPanel('editorOpenMap', {
      maps,
      onSelect: (id) => settle({ kind: 'catalog', id }),
      onImport: (map, fileName, notes) => settle({ kind: 'import', map, fileName, notes }),
      onImportError: (message) => options.onError?.(message),
      onCancel: () => settle(null),
    });
  });
}
